import * as localforage from 'localforage';
import { Router } from 'next/router';
import type { DeepNonNullable } from 'ts-essentials';
import { proxy } from 'valtio';
import { subscribeKey } from 'valtio/utils';

import type {
  OrderAppDeliveryMode,
  OrderAppGwpProduct,
} from '@/graphql/orderApp/api';
import type {
  CheckoutFragment,
  CheckoutLineFragment,
  MetadataItem,
} from '@/graphql/saleor/api';
import { ADD_TO_BAG_DIALOG_TIMEOUT, CacheKey, NFL } from '@/lib/const';
import { parseComplexMetadata } from '@/lib/metadata/helpers';
import type { INonAdyenGatewayCheckout } from '@/lib/payment/helpers';
import type {
  GatewayPaymentMethod,
  PaymentGatewayConfig,
  PaymentMethod,
} from '@/lib/payment/types';
import type { PreOrderData } from '@/lib/preOrder';
import type { ApiErrorWithType } from '@/lib/types';

export enum GWP {
  doNotNeedGift = 'doNotNeedGift',
}

export interface CheckoutState<Require = boolean> {
  checkout: Require extends false
    ? Partial<CheckoutFragment> | null
    : DeepNonNullable<CheckoutFragment>;
  deliveryMethods: OrderAppDeliveryMode[];
  errors: ApiErrorWithType[];
  gatewayCheckout: GatewayPaymentMethod | INonAdyenGatewayCheckout | null;
  gatewaysConfig: PaymentGatewayConfig[];
  gwpProduct: null | string | OrderAppGwpProduct;
  isAddPending?: boolean;
  isChangedAfterOrderCreated: boolean;
  isGuestCheckout: boolean;
  isIncludeNFLProduct: boolean;
  isIncludeWatch: boolean;
  isInitializing: boolean;
  isProcessing: boolean;
  linesCount: number;
  metadata: MetadataItem[];
  notification: string | null | CheckoutLineFragment[] | ApiErrorWithType[];
  notificationTimer: NodeJS.Timeout | null;
  orderId: undefined | string;
  paymentMethod: PaymentMethod | null;
  preOrderData: null | PreOrderData;
  voucherType: string;
}

export const initialState: CheckoutState = {
  /**
   * Set initializing initially to `true` to avoid layout shifts and smoothly
   * restore the checkout if the token is stored in the browser.
   */
  deliveryMethods: [],
  isInitializing: true,
  isProcessing: false,
  isGuestCheckout: true,
  checkout: null,
  metadata: [],
  notification: null,
  notificationTimer: null,
  linesCount: 0,
  gatewayCheckout: null,
  gwpProduct: null,
  errors: [],
  paymentMethod: null,
  preOrderData: null,
  gatewaysConfig: [],
  orderId: '',
  isIncludeWatch: false,
  isIncludeNFLProduct: false,
  isChangedAfterOrderCreated: false,
  isAddPending: false,
  voucherType: '',
};

export const checkoutState = proxy<CheckoutState>({
  ...initialState,
  get linesCount(): number {
    const count = (checkoutState.checkout?.lines as CheckoutLineFragment[])
      ?.filter(
        (line) =>
          !['buckle', 'gift', 'linesCount', 'colorado-tax-dummy']?.includes(
            line.variant.product.productType.slug,
          ) && !line.strapPackReference,
      )
      ?.reduce(
        (sum: number, line: CheckoutLineFragment) => sum + line.quantity,
        0,
      );
    return count ?? 0;
  },

  get preOrderData(): PreOrderData | null {
    const isPreOrder = checkoutState?.checkout?.orderType === 'PRE_ORDER';
    if (!isPreOrder) {
      return null;
    }

    const preOrderData = checkoutState.checkout?.preOrderData;
    if (preOrderData) {
      return parseComplexMetadata<PreOrderData>(preOrderData);
    }
    return null;
  },

  get isIncludeNFLProduct(): boolean {
    return (
      checkoutState.checkout?.lines?.some(
        (item) =>
          item.variant.product?.marketingPartner?.values?.[0]
            ?.externalReference === NFL,
      ) ?? false
    );
  },

  get isIncludeWatch(): boolean {
    return (
      checkoutState.checkout?.lines?.some(
        (item) =>
          item.variant.product.productType.name &&
          item.variant.product.productType.name.toLowerCase() === 'watch',
      ) ?? false
    );
  },

  get gwpProduct(): CheckoutState['gwpProduct'] {
    const gwp = checkoutState?.checkout?.gwpProduct;
    if (gwp) {
      if (gwp.toLowerCase() === 'false') {
        return GWP.doNotNeedGift;
      }
      return (
        checkoutState?.checkout?.gwpProduct &&
        JSON.parse(checkoutState?.checkout?.gwpProduct)
      );
    }
    return null;
  },
});

subscribeKey(
  checkoutState,
  'checkout',
  async (checkout: CheckoutState['checkout']) => {
    if (checkout?.user) {
      checkoutState.isGuestCheckout = false;
    }
  },
);

subscribeKey(
  checkoutState,
  'isChangedAfterOrderCreated',
  async (value: CheckoutState['isChangedAfterOrderCreated']) => {
    await localforage.setItem(CacheKey.IsChangedAfterOrderCreated, value);
  },
);

subscribeKey(
  checkoutState,
  'orderId',
  async (orderId: CheckoutState['orderId']) => {
    await localforage.setItem(CacheKey.OrderId, orderId);
  },
);

subscribeKey(
  checkoutState,
  'notification',
  (notification: CheckoutState['notification']) => {
    if (notification) {
      if (checkoutState.notificationTimer) {
        clearTimeout(checkoutState.notificationTimer);
      }

      checkoutState.notificationTimer = setTimeout(() => {
        if (checkoutState.notification) {
          checkoutState.notification = null;
        }

        if (checkoutState.notificationTimer) {
          clearTimeout(checkoutState.notificationTimer);
        }
      }, ADD_TO_BAG_DIALOG_TIMEOUT);
    }
  },
);

Router.events.on('routeChangeStart', () => (checkoutState.errors = []));
